import type { ReactNode } from 'react';

export type BannerTone = 'risk' | 'warn' | 'info' | 'ok' | 'gold';

interface Props {
  tone?: BannerTone;
  title?: string;
  children: ReactNode;
  /** Опциональный action в правом углу (CTA, ссылка, dismiss). */
  action?: ReactNode;
  /** ARIA role — alert для критичных, status для информационных. */
  role?: 'alert' | 'status';
}

/**
 * Banner — информер на странице. 5 тонов согласно `knowledge/STATES.md`:
 *  - risk    — критичные риски (банкротство, СДН-листинг)
 *  - warn    — предупреждения (просроченные суды, налоговый долг)
 *  - info    — нейтральные подсказки
 *  - ok      — позитивные сигналы (всё чисто)
 *  - gold    — premium-промо («попробуйте Pro 7 дней»)
 */
const TONES: Record<BannerTone, { bg: string; fg: string; border: string; icon: string }> = {
  risk: { bg: '#fde2e2', fg: '#a31919', border: '#e89191', icon: '⚠' },
  warn: { bg: '#fff5d9', fg: '#7a5a10', border: '#e6c870', icon: '⚠' },
  info: { bg: '#e3edff', fg: '#1c4a99', border: '#a8c1ee', icon: 'ℹ' },
  ok:   { bg: '#e6f4e9', fg: '#1f6b34', border: '#9ed4ad', icon: '✓' },
  gold: { bg: '#fff7e6', fg: '#7a5a10', border: '#c79a2c', icon: '★' },
};

export function Banner({ tone = 'info', title, children, action, role }: Props) {
  const tint = TONES[tone];
  const ariaRole = role ?? (tone === 'risk' ? 'alert' : 'status');
  return (
    <div
      role={ariaRole}
      className={`banner banner--${tone}`}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 16px',
        background: tint.bg,
        color: tint.fg,
        border: `1px solid ${tint.border}`,
        borderRadius: 8,
        fontSize: 14,
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1, marginTop: 1 }}>
        {tint.icon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && <div style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div>}
        <div style={{ overflowWrap: 'anywhere' }}>{children}</div>
      </div>
      {action && <div style={{ flexShrink: 0 }}>{action}</div>}
    </div>
  );
}
